import { useEffect, useState, type FormEvent } from "react";
import { fetchCnameChain, type CnameChain as Chain } from "../lib/cname-chain";

export function CnameChain() {
  const [host, setHost] = useState("");
  const [busy, setBusy] = useState(false);
  const [chain, setChain] = useState<Chain | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = (raw: string) => {
    const h = normalizeHost(raw);
    if (!h) return;
    setHost(h);
    setBusy(true);
    setChain(null);
    setError(null);
    history.replaceState(null, "", `?${new URLSearchParams({ host: h })}`);
    fetchCnameChain(h)
      .then((c) => setChain(c))
      .catch((e) => setError(String(e?.message ?? e)))
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    const h = new URLSearchParams(window.location.search).get("host");
    if (h) run(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    run(host);
  };

  // Render every name in the path, including the endpoint the last alias
  // points at (which is a target, not a queried hop).
  const steps = chain
    ? [
        ...chain.hops.map((h) => h.name),
        ...(chain.hops.length && chain.hops[chain.hops.length - 1].target ? [chain.finalName] : []),
      ]
    : [];

  return (
    <main className="console">
      <div className="input-row" role="search">
        <span className="input-row-label">CNAME</span>
        <form onSubmit={submit} className="lookup-form">
          <input
            type="text"
            placeholder="www.example.com"
            value={host}
            onChange={(e) => setHost(e.target.value)}
            autoFocus
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            aria-label="Hostname"
          />
          <button type="submit" disabled={busy || !host.trim()}>
            {busy ? "Tracing…" : "Trace"}
          </button>
        </form>
        <span className="input-row-hint" aria-hidden="true">
          <span className="stat-dot" /> follows every alias hop
        </span>
      </div>

      {error ? <div className="error" role="alert">{error}</div> : null}

      <div className="body-grid body-grid-single">
        <section className="table-panel">
          <div className="panel-title">
            CNAME CHAIN
            {chain ? <b> · {chain.host}</b> : null}
          </div>

          {!chain && !busy ? (
            <div className="empty-state">
              <p>
                Follows a hostname through every CNAME alias to the address at the end. A normal
                lookup shows you the final answer and hides the path — this shows the path, which is
                where the latency and the breakage live.
              </p>
              <p className="empty-hint">Try <code>www.microsoft.com</code>, <code>www.bbc.co.uk</code>, or your own hostname.</p>
            </div>
          ) : null}

          {busy ? <div className="empty-state"><p>Following the chain…</p></div> : null}

          {chain ? (
            <>
              <ol className="cname-path">
                {steps.map((name, i) => {
                  const last = i === steps.length - 1;
                  return (
                    <li key={`${name}-${i}`} className={last ? "cname-step cname-final" : "cname-step"}>
                      <span className="cname-index" aria-hidden="true">{i + 1}</span>
                      <code>{name}</code>
                      {!last ? <span className="cname-arrow" aria-hidden="true">↓ CNAME</span> : null}
                      {last && chain.loop ? <span className="cname-loop">loops back</span> : null}
                    </li>
                  );
                })}
              </ol>

              {!chain.loop ? (
                <table className="health-table">
                  <thead>
                    <tr><th>Endpoint</th><th>Type</th><th>Addresses</th></tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="ht-type" rowSpan={2}><code>{chain.finalName}</code></td>
                      <td className="ht-status">A</td>
                      <td className="ht-records">
                        {chain.addresses.a.length ? (
                          <ul>{chain.addresses.a.map((ip) => <li key={ip}><code>{ip}</code></li>)}</ul>
                        ) : <span className="ht-empty">none</span>}
                      </td>
                    </tr>
                    <tr>
                      <td className="ht-status">AAAA</td>
                      <td className="ht-records">
                        {chain.addresses.aaaa.length ? (
                          <ul>{chain.addresses.aaaa.map((ip) => <li key={ip}><code>{ip}</code></li>)}</ul>
                        ) : <span className="ht-empty">none</span>}
                      </td>
                    </tr>
                  </tbody>
                </table>
              ) : null}

              <ul className="health-findings">
                {chain.findings.map((f, i) => (
                  <li key={i} className={`finding finding-${f.level}`}>
                    <span className="finding-icon" aria-hidden="true">
                      {f.level === "good" ? "✓" : f.level === "warn" ? "!" : "·"}
                    </span>
                    {f.text}
                  </li>
                ))}
              </ul>

              <p className="whois-cross">
                Also check{" "}
                <a href={`/cname-lookup?host=${encodeURIComponent(chain.host)}`}>CNAME propagation</a> ·{" "}
                <a href={`/?host=${encodeURIComponent(chain.finalName)}&type=A`}>A records for the endpoint</a> ·{" "}
                <a href={`/dns-health?domain=${encodeURIComponent(chain.host)}`}>DNS health</a>.
              </p>
            </>
          ) : null}
        </section>
      </div>
    </main>
  );
}

function normalizeHost(raw: string): string {
  return raw.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "").replace(/\.$/, "");
}
